import { useState, useRef, useEffect, useCallback } from 'react';
import styles from './faqScreen.module.css';
import romiAvatar from '../../images/romi_4.webp';
import FormModal from '../../components/formModal/FormModal';
import useScrollReveal from '../../hooks/useScrollReveal';


function TypingIndicator() {
  return (
    <div className={styles.typingDots}>
      <span className={styles.dot} />
      <span className={styles.dot} />
      <span className={styles.dot} />
    </div>
  );
}

const faqs = [
  {
    q: 'אין לי שום ניסיון בתחום, זה בכלל מתאים לי?',
    a: 'לגמרי. גם אני התחלתי מאפס בלי שום רקע. זה לא עניין של כשרון מולד - זה עניין של תשוקה, תרגול והתמדה. אם את מרגישה שמשהו מושך אותך לתחום - סימן שזה בשבילך ושווה לבדוק התאמה.',
  },
  {
    q: 'תוך כמה זמן באמת אפשר להתחיל להרוויח?',
    a: 'לא אמכור לך חלום. את יוצאת מהקורס עם ערכה מלאה ויכולה להתחיל כבר למחרת, אבל בנייה של קהל לקוחות לוקחת זמן, תרגול ושיווק. החדשות הטובות? עם ליווי צמוד וכיוון נכון - את מקצרת את הדרך משמעותית.',
  },
  {
    q: 'מה קורה כשנגמר הקורס? מה אם אני לא אסתדר?',
    a: 'זה בדיוק מה שמבדיל אותי - אני לא נעלמת כשהקורס נגמר. אני איתך גם אחרי, זמינה לכל שאלה או רגע של חוסר ביטחון. יושבת איתך על איך מביאים לקוחות, איך משווקים נכון ואיך בונים נוכחות שעובדת לאורך זמן.',
  },
  {
    q: 'איך אני יודעת שזה הזמן הנכון להתחיל?',
    a: 'אם את שואלת את השאלה הזו - כנראה שזה כבר הזמן. אין רגע מושלם, יש רק את הרגע שבו מחליטים לעשות משהו אחרת. ויש אפשרות לפרוס לתשלומים נוחים, ככה שאפשר להתחיל בלי לשבור את הראש.',
  },
  {
    q: 'אני מפחדת שאני אלמד ובסוף לא אצליח',
    a: 'הפחד הזה טבעי לגמרי, גם אני הרגשתי ככה בהתחלה. אבל הבנות שמצליחות הן אלה שלא ויתרו גם כשהיה קשה. ההבדל הוא שאצלי את לא לבד - יש ליווי, יש מענה, ויש מישהי שדוחפת אותך קדימה גם ברגעים של ספק.',
  },
  {
    q: 'נכוויתי בעבר מקורס אחר, איך אדע שהפעם זה יהיה אחרת?',
    a: 'אני מבינה אותך לגמרי, גם אני נכוויתי בעבר ויודעת איך זה מרגיש. בדיוק בגלל זה בניתי את הקורסים שלי אחרת - כי ידעתי מה חסר לי כשאני למדתי. שווה שנדבר ותשאלי אותי כל מה שחשוב לך לפני שמחליטים.',
  },
  {
    q: 'הקורס רק בצפון? אני לא מהאזור',
    a: 'הקורסים מתקיימים בקליניקה שלי בצפון. אם המיקום בעייתי - שלחי לי הודעה ונבדוק ביחד אם אפשר למצוא פתרון שמתאים לך.',
  },
  {
    q: 'איך אני יודעת שזה לא עוד קורס שנגמר עם תעודה במגירה?',
    a: 'כי מה שקורה אצלי אחרי הקורס חשוב לא פחות מהקורס עצמו. את יוצאת עם ערכת עבודה מלאה, ידע מעשי, תשתית שיווקית ומישהי שנשארת לצדך. עשרות בנות שעברו אצלי כבר עובדות ומתפרנסות מהתחום היום.',
  },
];

function FaqScreen() {
  const [openIndex, setOpenIndex] = useState(null);
  const [typingIndex, setTypingIndex] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const typingTimeoutRef = useRef(null);
  const sectionRef = useRef(null);
  useScrollReveal(sectionRef);

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };
  }, []);

  const handleQuestionClick = useCallback((i) => {
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);

    if (openIndex === i) {
      setOpenIndex(null);
      setTypingIndex(null);
      return;
    }

    setOpenIndex(null);
    setTypingIndex(i);

    typingTimeoutRef.current = setTimeout(() => {
      setTypingIndex(null);
      setOpenIndex(i);
    }, 800);
  }, [openIndex]);

  return (
    <>
    <section className={styles.faq} id="faq" ref={sectionRef}>
      <div className={styles.bgOrb1} />
      <div className={styles.bgOrb2} />
      <div className={styles.bgOrb3} />

      <div className={styles.container}>
          <div className={styles.header} data-sr="0">
            <h2 className={styles.title}>
              שאלות <em className={styles.em}>נפוצות</em>
            </h2>
            <p className={styles.subtitle}>אני בטוחה שאחת מהשאלות כאן עברה לך בראש במהלך הדף - אז כל התשובות כאן</p>
            <p className={styles.description}>לחצי על כל שאלה והתשובה תיפתח</p>
          </div>

          <div className={styles.chatFrame} data-sr="0.08">
            <div className={styles.messagesArea}>
              {faqs.map((faq, i) => (
                  <div key={i} className={styles.messageGroup} data-sr={String(0.04 + i * 0.06)} data-sr-y="20">
                    <div className={styles.questionRow}>
                      <button
                        className={`${styles.questionBubble} ${openIndex === i ? styles.questionBubbleActive : ''}`}
                        onClick={() => handleQuestionClick(i)}
                      >
                        {faq.q}
                      </button>
                    </div>

                    {typingIndex === i && (
                      <div className={`${styles.answerRow} ${styles.typingEnter}`}>
                        <img src={romiAvatar} alt="" className={styles.romiAvatar} loading="lazy" />
                        <div className={styles.answerBubble}>
                          <TypingIndicator />
                        </div>
                      </div>
                    )}

                    {openIndex === i && (
                      <div className={`${styles.answerRow} ${styles.answerEnter}`}>
                        <img src={romiAvatar} alt="" className={styles.romiAvatar} loading="lazy" />
                        <div className={styles.answerBubble}>
                          <p>{faq.a}</p>
                        </div>
                      </div>
                    )}
                  </div>
              ))}

                <div className={styles.ctaMessageGroup} data-sr={String(0.04 + faqs.length * 0.06)} data-sr-y="20">
                  <div className={styles.answerRow}>
                    <img src={romiAvatar} alt="" className={styles.romiAvatar} loading="lazy" />
                    <div className={styles.ctaBubble}>
                      <p className={styles.ctaText}>עוד שאלות? אני תמיד פה</p>
                      <button
                        className={styles.ctaButton}
                        onClick={() => setIsFormOpen(true)}
                      >
                        <span>דברי איתי</span>
                      </button>
                    </div>
                  </div>
                </div>
            </div>
          </div>
      </div>
    </section>

    <FormModal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} />
    </>
  );
}

export default FaqScreen;
